import React, { useState, useEffect } from 'react';
import { Crown, Clock, Check, AlertCircle, CreditCard, ChevronRight, Info } from 'lucide-react';
import { useTheme, Btn, fmt } from './shared';
import { S } from './strings';
import * as api from './api';

// Дефолты — используются пока не загрузятся настройки из админки
const DEFAULT_PRICE_FIRST = 5500;
const DEFAULT_PRICE_EXTRA = 3500;
const DEFAULT_DISCOUNTS = { 1: 0, 3: 0.10, 6: 0.15, 12: 0.20 };

function calcPrice(orgCount, months, priceFirst, priceExtra, discounts) {
  const c = Math.max(1, orgCount);
  const monthly = priceFirst + Math.max(0, c - 1) * priceExtra;
  const discount = discounts[String(months)] || discounts[months] || 0;
  const total = Math.round(monthly * months * (1 - discount));
  const fullPrice = monthly * months;
  const saved = fullPrice - total;
  return { monthly, total, discount, fullPrice, saved };
}

export default function SubscriptionTab({ globalUser, userOrgs, isCompact }) {
  const t = useTheme();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [sec, setSec] = useState('plan');
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [loading, setLoading] = useState(true);
  // Выбор организаций — по умолчанию все выбраны
  const [selectedOrgIds, setSelectedOrgIds] = useState(() => (userOrgs || []).map(o => o.id));
  const [bonusBalance, setBonusBalance] = useState(0);
  const [useBonuses, setUseBonuses] = useState(true);
  // Динамические цены из админки
  const [priceFirst, setPriceFirst] = useState(DEFAULT_PRICE_FIRST);
  const [priceExtra, setPriceExtra] = useState(DEFAULT_PRICE_EXTRA);
  const [discounts, setDiscounts] = useState(DEFAULT_DISCOUNTS);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getSubscriptionStatus(),
      api.getPaymentHistory(),
      api.getSettings(),
    ]).then(([s, h, settings]) => {
      setStatus(s);
      setHistory(h || []);
      if (settings) {
        if (settings.priceFirstOrg) setPriceFirst(settings.priceFirstOrg);
        if (settings.priceExtraOrg) setPriceExtra(settings.priceExtraOrg);
        if (settings.discounts && typeof settings.discounts === 'object') setDiscounts(settings.discounts);
      }
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.getReferralInfo()
      .then(data => setBonusBalance(Number(data.bonusBalance || 0)))
      .catch(() => setBonusBalance(0));
  }, []);

  // Если userOrgs обновились а selectedOrgIds пуст — инициализируем
  useEffect(() => {
    if (userOrgs && userOrgs.length > 0 && selectedOrgIds.length === 0) {
      setSelectedOrgIds(userOrgs.map(o => o.id));
    }
  }, [userOrgs, selectedOrgIds.length]);

  const toggleOrg = (orgId) => {
    setSelectedOrgIds(prev => {
      if (prev.includes(orgId)) {
        return prev.filter(id => id !== orgId);
      }
      return [...prev, orgId];
    });
  };

  const selCount = Math.max(selectedOrgIds.length, 0);
  const pricing = calcPrice(selCount, selectedMonths, priceFirst, priceExtra, discounts);

  // Собираем периоды и бейджи из discounts
  const periods = Object.keys(discounts).map(Number).sort((a, b) => a - b);
  // Если нет периода 1, добавим
  if (!periods.includes(1)) periods.unshift(1);

  // Бонус preview
  const totalWithPeriod = Number(pricing.total || 0);
  const maxBonusUse = Math.max(0, totalWithPeriod - 100);
  const bonusToApply = useBonuses ? Math.min(bonusBalance, maxBonusUse) : 0;
  const finalAmount = totalWithPeriod - bonusToApply;

  const statusColor = !status ? t.textMuted
    : status.subscriptionStatus === 'active' ? t.accentGreen
    : status.subscriptionStatus === 'trial' ? t.accent
    : t.accentRed;

  const statusLabel = !status ? '...'
    : status.subscriptionStatus === 'trial' ? S().subscription.statusTrial
    : status.subscriptionStatus === 'active' ? S().subscription.statusActive
    : S().subscription.statusExpired;

  const secBtns = [
    { id: 'plan', label: S().subscription.planTab },
    { id: 'history', label: S().subscription.historyTab },
  ];

  return (
    <div style={{ padding: isCompact ? 12 : 24, maxWidth: 800 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: `${t.accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Crown size={20} color={t.accent} />
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: t.text }}>{S().subscription.title}</div>
          <div style={{ fontSize: 13, color: t.textSec }}>{S().subscription.subtitle}</div>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: t.bgInput, borderRadius: 10, padding: 3 }}>
        {secBtns.map(b => (
          <button key={b.id} onClick={() => setSec(b.id)} style={{
            flex: 1, padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: sec === b.id ? 700 : 500,
            background: sec === b.id ? t.bgCard : 'transparent', color: sec === b.id ? t.accent : t.textMuted,
            boxShadow: sec === b.id ? t.shadow : 'none', transition: '0.2s', cursor: 'pointer',
          }}>{b.label}</button>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: t.textMuted }}>{S().common.loading}</div>}

      {/* ═══ PLAN TAB ═══ */}
      {!loading && sec === 'plan' && (
        <div style={{ display: 'grid', gap: 16 }}>

          {/* Current status card */}
          <div style={{ padding: 20, background: t.bgCard, borderRadius: 14, border: `2px solid ${statusColor}40`, boxShadow: t.shadow }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${statusColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {(status?.subscriptionStatus === 'expired' || status?.subscriptionStatus === 'free') ? <AlertCircle size={18} color={statusColor} /> : <Check size={18} color={statusColor} />}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: statusColor }}>{statusLabel}</div>
                {status?.daysLeft > 0 && (
                  <div style={{ fontSize: 13, color: t.textSec }}>
                    {S().subscription.daysLeft(status.daysLeft)}
                    {status.subscriptionExpiresAt && ` \u2022 ${S().subscription.expiresAt} ${new Date(status.subscriptionExpiresAt).toLocaleDateString()}`}
                  </div>
                )}
                {(status?.subscriptionStatus === 'expired' || status?.subscriptionStatus === 'free') && (
                  <div style={{ fontSize: 13, color: t.accentRed }}>{S().subscription.expiredHint}</div>
                )}
              </div>
            </div>
          </div>

          {/* ─── Organization selector ─── */}
          <div style={{ padding: 16, background: t.bgCard, borderRadius: 14, border: `1px solid ${t.border}`, boxShadow: t.shadow }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 6 }}>
              {S().subscription.selectOrgs}
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Info size={13} color={t.textMuted} />
              {S().subscription.selectOrgsHint}
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              {(userOrgs || []).map((org, idx) => {
                const checked = selectedOrgIds.includes(org.id);
                const isFirst = (() => {
                  // Среди выбранных — первая по порядку = 5500, остальные = 3500
                  const selIdx = selectedOrgIds.indexOf(org.id);
                  if (selIdx === -1) return false;
                  return selIdx === 0;
                })();
                const priceLabel = checked
                  ? (isFirst ? fmt(priceFirst) : fmt(priceExtra))
                  : null;

                return (
                  <label key={org.id} onClick={() => toggleOrg(org.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10,
                    border: `1.5px solid ${checked ? t.accent : t.border}`,
                    background: checked ? `${t.accent}08` : t.bgInput,
                    cursor: 'pointer', transition: '0.15s',
                  }}>
                    {/* Checkbox */}
                    <div style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      border: `2px solid ${checked ? t.accent : t.textMuted}`,
                      background: checked ? t.accent : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: '0.15s',
                    }}>
                      {checked && <Check size={14} color="#fff" strokeWidth={3} />}
                    </div>

                    {/* Org color dot + name */}
                    <div style={{
                      width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                      background: org.color || t.accent,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 11, fontWeight: 800,
                    }}>
                      {org.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.name}</div>
                    </div>

                    {/* Price per org */}
                    {priceLabel && (
                      <div style={{ fontSize: 13, fontWeight: 700, color: isFirst ? t.accent : t.textSec, whiteSpace: 'nowrap' }}>
                        {priceLabel} / {S().subscription.month}
                      </div>
                    )}
                  </label>
                );
              })}
            </div>

            {/* Подсказка если ничего не выбрано */}
            {selCount === 0 && (
              <div style={{ fontSize: 12, color: t.accentRed, marginTop: 8, fontWeight: 600 }}>
                {S().subscription.selectAtLeast1}
              </div>
            )}
          </div>

          {/* ─── Pricing summary ─── */}
          <div style={{ padding: 16, background: t.bgInput, borderRadius: 12, border: `1px solid ${t.border}` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 10 }}>
              <CreditCard size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {S().subscription.pricingTitle}
            </div>
            <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: t.textSec }}>
                <span>{S().subscription.firstOrg}</span>
                <span style={{ fontWeight: 700, color: t.text }}>{fmt(priceFirst)} / {S().subscription.month}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: t.textSec }}>
                <span>{S().subscription.extraOrg}</span>
                <span style={{ fontWeight: 700, color: t.text }}>{fmt(priceExtra)} / {S().subscription.month}</span>
              </div>
              {selCount > 0 && (
                <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 700, color: t.text }}>{S().subscription.selectedOrgs(selCount)}</span>
                  <span style={{ fontWeight: 800, color: t.accent }}>{fmt(pricing.monthly)} / {S().subscription.month}</span>
                </div>
              )}
            </div>
          </div>

          {/* ─── Period selector ─── */}
          {selCount > 0 && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 10 }}>{S().subscription.choosePeriod}</div>
              <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr 1fr' : `repeat(${Math.min(periods.length, 4)}, 1fr)`, gap: 10 }}>
                {periods.map(m => {
                  const p = calcPrice(selCount, m, priceFirst, priceExtra, discounts);
                  const sel = selectedMonths === m;
                  const d = discounts[String(m)] || discounts[m] || 0;
                  const badge = d > 0 ? `-${Math.round(d * 100)}%` : null;
                  const perMonth = Math.round(p.total / m);
                  return (
                    <button key={m} onClick={() => setSelectedMonths(m)} style={{
                      padding: '14px 12px', borderRadius: 12, textAlign: 'center', cursor: 'pointer',
                      background: sel ? `${t.accent}12` : t.bgCard,
                      border: `2px solid ${sel ? t.accent : t.border}`,
                      transition: '0.2s', position: 'relative',
                    }}>
                      {badge && (
                        <div style={{ position: 'absolute', top: -8, right: 8, background: t.accentGreen, color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 10 }}>{badge}</div>
                      )}
                      <div style={{ fontSize: 15, fontWeight: 800, color: sel ? t.accent : t.text }}>
                        {m === 1 ? S().subscription.month1 : S().subscription.monthN ? S().subscription.monthN(m) : m + ' ' + S().subscription.monthsLabel}
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: sel ? t.accent : t.text, marginTop: 6 }}>{fmt(p.total)}</div>
                      {m > 1 && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{fmt(perMonth)} / {S().subscription.month}</div>}
                      {p.saved > 0 && (
                        <div style={{ fontSize: 11, fontWeight: 700, color: t.accentGreen, marginTop: 4 }}>
                          {S().subscription.savings(fmt(p.saved))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Pay button ─── */}
          {selCount > 0 && (
            <div style={{ padding: 20, background: t.bgCard, borderRadius: 14, border: `1px solid ${t.border}`, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: t.accent, marginBottom: 4 }}>
                {fmt(pricing.total)}
              </div>
              <div style={{ fontSize: 13, color: t.textSec, marginBottom: 16 }}>
                {S().subscription.totalFor(selectedMonths)}
              </div>

              {bonusBalance > 0 && (
                <div style={{ marginTop: 12, marginBottom: 16, padding: 12, background: `${t.accent}10`, border: `1px solid ${t.accent}40`, borderRadius: 10, textAlign: 'left' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: (useBonuses && bonusToApply > 0) ? 8 : 0 }}>
                    <div
                      onClick={(e) => { e.preventDefault(); setUseBonuses(!useBonuses); }}
                      style={{
                        width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                        border: `2px solid ${useBonuses ? t.accent : t.textMuted}`,
                        background: useBonuses ? t.accent : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: '0.15s',
                      }}
                    >
                      {useBonuses && <Check size={13} color="#fff" strokeWidth={3} />}
                    </div>
                    <div style={{ flex: 1, fontSize: 13, color: t.text, fontWeight: 600 }}>
                      {S().subscription.useBonuses}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.accent }}>
                      {S().subscription.bonusAvailable(fmt(bonusBalance))}
                    </div>
                  </label>
                  {useBonuses && bonusToApply > 0 && (
                    <div style={{ display: 'grid', gap: 4, fontSize: 12, color: t.textSec, paddingTop: 8, borderTop: `1px solid ${t.accent}30` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{S().subscription.basePrice}</span>
                        <span>{fmt(totalWithPeriod)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: t.accent }}>
                        <span>{S().subscription.bonusDiscount}</span>
                        <span>−{fmt(bonusToApply)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 800, color: t.text, paddingTop: 4, borderTop: `1px solid ${t.accent}30`, marginTop: 2 }}>
                        <span>{S().subscription.finalPrice}</span>
                        <span>{fmt(finalAmount)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TODO: редирект на страницу оплаты PayBox/Kaspi */}
              <Btn variant="primary" style={{ width: '100%', padding: 14, fontSize: 15, justifyContent: 'center', opacity: 0.6, cursor: 'not-allowed' }}>
                <CreditCard size={16} /> {S().subscription.payBtn}
              </Btn>
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 10, lineHeight: 1.5 }}>
                {S().subscription.payDisabledHint}
              </div>
            </div>
          )}

        </div>
      )}

      {/* ═══ HISTORY TAB ═══ */}
      {!loading && sec === 'history' && (
        <div>
          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: t.textMuted }}>
              <Clock size={32} color={t.textMuted} style={{ marginBottom: 8 }} />
              <div>{S().subscription.historyEmpty}</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {history.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: t.bgCard, borderRadius: 10, border: `1px solid ${t.border}` }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: p.status === 'success' ? `${t.accentGreen}20` : p.status === 'pending' ? `${t.accent}20` : `${t.accentRed}20`,
                  }}>
                    {p.status === 'success' ? <Check size={16} color={t.accentGreen} /> : p.status === 'pending' ? <Clock size={16} color={t.accent} /> : <AlertCircle size={16} color={t.accentRed} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{fmt(p.amount)}</div>
                    <div style={{ fontSize: 12, color: t.textMuted }}>
                      {p.subscriptionMonths} {S().subscription.monthsLabel} \u2022 {new Date(p.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                    background: p.status === 'success' ? `${t.accentGreen}15` : p.status === 'pending' ? `${t.accent}15` : `${t.accentRed}15`,
                    color: p.status === 'success' ? t.accentGreen : p.status === 'pending' ? t.accent : t.accentRed,
                  }}>
                    {p.status === 'success' ? S().subscription.paymentSuccess : p.status === 'pending' ? S().subscription.paymentPending : S().subscription.paymentFailed}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
